/**
 * config/navTree.ts — the left-rail nav tree as DATA (feat/rail-redesign-v2).
 *
 * The rail used to be a wall of hardcoded `renderNavItem(...)` calls inside the
 * (frozen) AppShell. This module makes the rail's STRUCTURE, LABELS, ROUTES and
 * per-item VISIBILITY declarative, so AppShell only owns presentation (icons,
 * tooltips, badges, collapse chrome) and renders whatever `resolveNav()` hands
 * back. New surfaces, renames and permission tweaks land here, not in the shell.
 *
 * Two product principles drive the shape:
 *  - Tools vs Departments: a top-level node is either a flat TOOL (one click,
 *    opens directly) or a collapsible DEPARTMENT GROUP that reveals sub-items.
 *  - Hide, don't lock: an item/group the role can't access is ABSENT from the
 *    resolved tree (never greyed, never badged). A group with zero visible
 *    items hides its header too. The resolver is the single choke point.
 *
 * Gates read the existing tab/action permission vocabulary (AuthContext +
 * UserManagement), so the owner tunes each grant in the permissions UI without
 * a code change — this file seeds sensible defaults, not a frozen policy.
 */

/** The auth-derived facts a gate may read (a thin slice of useAuth()). */
export interface NavGateContext {
  hasTab: (tab: string) => boolean;
  hasAction: (action: string) => boolean;
  isAdmin: () => boolean;
  isTeamMember: () => boolean;
}

export type NavGate = (ctx: NavGateContext) => boolean;

/** Which live unread/count badge (if any) the shell should hang on an item. */
export type NavBadge = 'inbox' | 'helpdesk' | 'forms';

/** Surface families a user can pin a deep instance under (Phase 2 favorites). */
export type PinParent = 'pipelines' | 'campaigns' | 'boards' | 'clients';

export interface NavItem {
  /** Stable id (React key + selection match fallback). */
  key: string;
  label: string;
  /** Absolute navigation target. Coming-soon items still carry the eventual path. */
  path: string;
  /** Looked up in AppShell's icon map; falls back to a default glyph. */
  iconKey: string;
  visible: NavGate;
  /** Honest "· soon" entry: visible + gated, but not yet navigable (design-system). */
  comingSoon?: boolean;
  badge?: NavBadge;
  /** Phase 2: instances of this surface can be pinned under it in the rail. */
  pinParent?: PinParent;
}

/** A collapsible sub-section inside a group (one level of nesting, e.g. EOS › Meetings). */
export interface NavSubGroup {
  id: string;
  label: string;
  visible: NavGate;
  items: NavItem[];
}

export interface NavGroup {
  id: string;
  label: string;
  visible: NavGate;
  /** Direct items, rendered first (above any sub-groups). */
  items: NavItem[];
  /** Optional nested sub-sections (e.g. EOS › Meetings / EOS Toolbox). */
  subgroups?: NavSubGroup[];
}

export type NavNode =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; group: NavGroup };

// ── gate helpers ───────────────────────────────────────────────────────────
const ALWAYS: NavGate = () => true;
const tab = (t: string): NavGate => (c) => c.hasTab(t);
const anyTab = (...ts: string[]): NavGate => (c) => ts.some((t) => c.hasTab(t));
const action = (a: string): NavGate => (c) => c.hasAction(a);
/** Leadership-only: the EOS leadership action OR an admin role. */
const LEADERSHIP: NavGate = (c) => c.hasAction('view_eos_leadership_l10') || c.isAdmin();

/**
 * THE TREE. Order is render order. Flat top-level items are TOOLS; `group`
 * nodes are collapsible DEPARTMENTS. Visible labels are exactly as the product
 * owner specified (rail v2). Internal `key`s stay stable to avoid churning the
 * AppShell dispatch / route map (e.g. the Email tool keeps key 'PersonalInbox').
 */
export const NAV_TREE: NavNode[] = [
  // ── General (the everyday tools). Grouped (not flat) so every rail entry
  //    lives in a collapsible group — a user favorites what they use, then
  //    collapses the rest for a clean, personal rail (owner direction 2026-06-14). ──
  {
    kind: 'group',
    group: {
      id: 'General', label: 'General', visible: ALWAYS, items: [
        { key: 'Dashboard', label: 'Center Console', path: '/dashboard', iconKey: 'Dashboard', visible: ALWAYS },
        { key: 'PersonalInbox', label: 'Email', path: '/email', iconKey: 'Email', visible: anyTab('personal_inbox', 'inbox'), badge: 'inbox' },
        { key: 'Time Tracking', label: 'Time Tracker', path: '/time-tracking', iconKey: 'Time Tracking', visible: tab('time_tracking') },
        { key: 'Todos', label: 'To-Dos', path: '/todos', iconKey: 'Todos', visible: ALWAYS },
        { key: 'KnowledgeBase', label: 'Mental Herd', path: '/knowledge-base', iconKey: 'Mental Herd', visible: ALWAYS },
      ],
    },
  },

  // ── Tools ─────────────────────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Tools', label: 'Tools', visible: ALWAYS, items: [
        { key: 'Comms', label: 'Comms', path: '/comms', iconKey: 'Comms', visible: tab('comms') },
        { key: 'Forms', label: 'Forms', path: '/forms', iconKey: 'Forms', visible: ALWAYS, badge: 'forms' },
        { key: 'Call Intelligence', label: 'Lord of the Notes', path: '/call-intelligence', iconKey: 'Call Intelligence', visible: tab('call_intelligence') },
        { key: 'Scheduling', label: 'Bookings', path: '/scheduling', iconKey: 'Scheduling', visible: ALWAYS },
        // Calendar surface (Velvet Grove v1) — the unified day/week/month/agenda
        // aggregate. Mounted as a Scheduling sub-path (top-level /calendar
        // dispatch needs an AppShell unlock; deferred). /calendar redirects here.
        { key: 'Calendar', label: 'Calendar', path: '/scheduling/calendar', iconKey: 'Scheduling', visible: tab('calendar') },
      ],
    },
  },

  // ── AI Mission Control (its own top-level department, 2026-06-22). Global
  //     AI usage + the agent tiles in one place. Gated view_ai_agents (MC-QA-1
  //     §5) so read-holders — not only full settings-admins — can reach it;
  //     hide-don't-lock. The in-page tabs (Mission Control / AI Agents) are not
  //     rail items. ──
  {
    kind: 'group',
    group: {
      id: 'AI', label: 'AI', visible: action('view_ai_agents'), items: [
        { key: 'AiMissionControl', label: 'Mission Control', path: '/mission-control', iconKey: 'AiMissionControl', visible: action('view_ai_agents') },
        // Mission Control 2.0 (Entry 536): the ledger is Spend, a Mission Control tab; its door left the rail.
      ],
    },
  },

  // ── EOS (its own top-level domain; de-noised into a short rail list + two
  //     collapsible sub-sections, per owner direction 2026-06-14). The trio
  //     Scorecards/Rocks/V-TO sits directly in the rail; meetings and the rest
  //     fold into Meetings / EOS Toolbox. Leadership-only items stay gated. ──
  {
    kind: 'group',
    group: {
      id: 'EOS', label: 'EOS', visible: tab('eos'),
      items: [
        { key: 'EosScorecards', label: 'Scorecards', path: '/analytics/scorecard', iconKey: 'Scorecards', visible: ALWAYS },
        { key: 'EosRocks', label: 'Rocks', path: '/eos/rocks', iconKey: 'Rocks', visible: ALWAYS },
        { key: 'EosVto', label: 'V/TO', path: '/eos/vto', iconKey: 'EOS', visible: ALWAYS },
      ],
      subgroups: [
        {
          id: 'EOS Meetings', label: 'Meetings', visible: ALWAYS, items: [
            { key: 'EosL10Ops', label: 'L10s — Ops', path: '/eos/meetings', iconKey: 'Meetings', visible: ALWAYS },
            { key: 'EosL10Sales', label: 'L10s — Sales & Marketing', path: '/eos/meetings', iconKey: 'Meetings', visible: ALWAYS },
            { key: 'EosKickoff', label: 'Kickoff Meeting', path: '/eos/kickoff', iconKey: 'Meetings', visible: ALWAYS },
            { key: 'EosLeadershipL10', label: 'Leadership L10s', path: '/eos/meetings', iconKey: 'Meetings', visible: LEADERSHIP },
            // DS2EOS Home — Quarterly became Onsites and annuals live there (the path
            // stays), so the separate "Annual Meetings · soon" tease is gone. 1:1
            // reviews are live and everyone's: each person sees their own.
            { key: 'EosQuarterly', label: 'Onsites', path: '/eos/quarterly', iconKey: 'Meetings', visible: LEADERSHIP },
            { key: 'EosOneOnOne', label: '1:1 reviews', path: '/eos/one-on-ones', iconKey: 'EOS', visible: ALWAYS },
          ],
        },
        {
          id: 'EOS Toolbox', label: 'EOS Toolbox', visible: ALWAYS, items: [
            { key: 'EosIds', label: 'IDS', path: '/eos/ids', iconKey: 'IDS', visible: ALWAYS },
            { key: 'EosAccountability', label: 'Accountability Chart', path: '/eos/accountability', iconKey: 'EOS', visible: ALWAYS },
            // Track B — the People review replaces the `EosGwc` coming-soon
            // tease: GWC (gets it · wants it · capacity) is three of its
            // columns, and the real surface answers all six per seat. Leadership
            // only, so a team member sees nothing here rather than a locked row.
            { key: 'EosPeople', label: 'People review', path: '/eos/people', iconKey: 'EOS', visible: LEADERSHIP },
            // DS2EOS Home — live, and leadership's (or whoever can run a round).
            { key: 'EosAssessments', label: 'EOS assessment', path: '/eos/assessments', iconKey: 'EOS', visible: (c) => LEADERSHIP(c) || c.hasAction('manage_eos_assessments') },
          ],
        },
      ],
    },
  },

  // ── Operations ──────────────────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Operations', label: 'Operations', visible: tab('operations'), items: [
        { key: 'HelpDesk', label: 'Help Desk', path: '/helpdesk', iconKey: 'HelpDesk', visible: anyTab('helpdesk', 'inbox'), badge: 'helpdesk' },
        { key: 'OpsProjects', label: 'Projects', path: '/operations/projects', iconKey: 'Projects', visible: ALWAYS, pinParent: 'boards' },
        { key: 'OpsTickets', label: 'Support Tickets', path: '/operations/tickets', iconKey: 'Tickets', visible: ALWAYS },
        { key: 'Clients', label: 'Clients', path: '/clients', iconKey: 'Clients', visible: ALWAYS, pinParent: 'clients' },
        { key: 'Documentation Nation', label: 'Documentation', path: '/documentation', iconKey: 'Documentation Nation', visible: ALWAYS },
      ],
    },
  },

  // ── Sales & Marketing ───────────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Sales & Marketing', label: 'Sales & Marketing', visible: anyTab('sales', 'marketing'), items: [
        // BSG Deals c4 (2026-09-18): the board renders at bare /sales; the key
        // stays 'SalesPipelines' so saved pins/layouts and the ⌘K palette's
        // derived "Go to …" entry carry over unchanged.
        { key: 'SalesPipelines', label: 'BSG Deals', path: '/sales', iconKey: 'Sales', visible: tab('sales'), pinParent: 'pipelines' },
        // EVENT-1 c2 (2026-10-19): conferences as records — the list and each
        // event's booth board. Reads ride tab:sales (the router's own mount).
        { key: 'SalesEvents', label: 'Events', path: '/sales/events', iconKey: 'Meetings', visible: tab('sales') },
        { key: 'Campaigns', label: 'Campaigns', path: '/sales/campaigns', iconKey: 'Marketing', visible: anyTab('sales', 'marketing'), pinParent: 'campaigns' },
        { key: 'Affiliates', label: 'Affiliates', path: '/sales/affiliates', iconKey: 'Marketing', visible: action('view_affiliates') },
        { key: 'ContentSuite', label: 'Content Suite', path: '/marketing/content', iconKey: 'Marketing', visible: tab('marketing') },
        { key: 'Newsletters', label: 'Newsletters', path: '/marketing/newsletters', iconKey: 'Marketing', visible: tab('marketing') },
        { key: 'Proposals', label: 'Contracts', path: '/contracts', iconKey: 'Proposals', visible: tab('sales') },
      ],
    },
  },

  // ── Data (raw tables) ───────────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Data', label: 'Data', visible: anyTab('sales', 'operations', 'marketing'), items: [
        { key: 'Contacts', label: 'Contacts', path: '/contacts', iconKey: 'Contacts', visible: anyTab('sales', 'marketing', 'operations') },
        { key: 'Templates', label: 'Templates', path: '/templates', iconKey: 'Templates', visible: ALWAYS },
      ],
    },
  },

  // ── Analytics ───────────────────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Analytics', label: 'Analytics', visible: anyTab('scorecards', 'insights', 'forecasting', 'capacity', 'finances', 'time_reports'), items: [
        { key: 'Scorecards', label: 'Scorecards', path: '/analytics/scorecard', iconKey: 'Scorecards', visible: tab('scorecards') },
        // Forecasting IS the Capacity & Forecasting tool now — it replaced the old
        // hours-based forecasting surface (2026-07-04). Keeps key 'Capacity' so the
        // AppShell reportSubPath='capacity' dispatch is unchanged; gated on either
        // tab so nobody who could reach the old Forecasting loses the entry.
        { key: 'Capacity', label: 'Forecasting', path: '/analytics/forecasting', iconKey: 'Forecasting', visible: anyTab('forecasting', 'capacity') },
        { key: 'Time Reports', label: 'Time Reports', path: '/analytics/time-reports', iconKey: 'Time Reports', visible: anyTab('time_reports', 'time_tracking') },
        { key: 'Finances', label: 'Finances', path: '/analytics/finances', iconKey: 'Finances', visible: tab('finances') },
      ],
    },
  },

  // ── Admin (leadership/owner) ──────────────────────────────────────────────────
  {
    kind: 'group',
    group: {
      id: 'Admin', label: 'Admin', visible: (c) => c.isAdmin() || c.hasTab('engineering'), items: [
        { key: 'Engineering', label: 'Engineering', path: '/engineering', iconKey: 'Engineering', visible: tab('engineering') },
        { key: 'SmokeTests', label: 'Smoke Tests', path: '/smoke-tests', iconKey: 'SmokeTests', visible: action('manage_settings') },
      ],
    },
  },
];

/**
 * Resolve the tree for a given auth context: drop items the role can't see,
 * and drop any group left empty (so no orphan header renders). This is the
 * single hide-don't-lock choke point for the rail.
 */
export function resolveNav(ctx: NavGateContext): NavNode[] {
  const out: NavNode[] = [];
  for (const node of NAV_TREE) {
    if (node.kind === 'item') {
      if (node.item.visible(ctx)) out.push(node);
      continue;
    }
    if (!node.group.visible(ctx)) continue;
    const items = node.group.items.filter((i) => i.visible(ctx));
    const subgroups = (node.group.subgroups ?? [])
      .filter((sg) => sg.visible(ctx))
      .map((sg) => ({ ...sg, items: sg.items.filter((i) => i.visible(ctx)) }))
      .filter((sg) => sg.items.length > 0);
    if (items.length === 0 && subgroups.length === 0) continue;
    out.push({ kind: 'group', group: { ...node.group, items, subgroups } });
  }
  return out;
}

/** Every NavItem in the tree (flat + group direct + sub-group), unfiltered. */
export function allNavItems(): NavItem[] {
  const out: NavItem[] = [];
  for (const node of NAV_TREE) {
    if (node.kind === 'item') { out.push(node.item); continue; }
    out.push(...node.group.items);
    for (const sg of node.group.subgroups ?? []) out.push(...sg.items);
  }
  return out;
}

/** Look a nav item up by its stable key (used to resolve a starred item's path/label). */
export function navItemByKey(key: string): NavItem | undefined {
  return allNavItems().find((i) => i.key === key);
}

/** A top-level node's stable id (group id, or flat item key) for layout ordering. */
export function nodeId(node: NavNode): string {
  return node.kind === 'group' ? node.group.id : node.item.key;
}

export interface RailLayout {
  group_order: string[];
  item_order: Record<string, string[]>;
}

/**
 * Apply a per-user layout (Phase 5 drag-reorder) to the resolved tree: reorder
 * top-level nodes by `group_order` and each group's direct items by
 * `item_order[groupId]`. Ids not in the saved order keep their default relative
 * position (sorted to the end, stably), so a nav-tree change never drops or
 * scrambles a saved layout — new entries simply append.
 */
export function applyLayout(nodes: NavNode[], layout?: RailLayout | null): NavNode[] {
  const groupOrder = layout?.group_order ?? [];
  const itemOrder = layout?.item_order ?? {};

  const rank = (id: string, order: string[]): number => {
    const i = order.indexOf(id);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };

  const orderedTop = nodes
    .map((n, i) => ({ n, i }))
    .sort((a, b) => {
      const ra = rank(nodeId(a.n), groupOrder);
      const rb = rank(nodeId(b.n), groupOrder);
      return ra === rb ? a.i - b.i : ra - rb; // stable for unknowns
    })
    .map((x) => x.n);

  return orderedTop.map((n) => {
    if (n.kind !== 'group') return n;
    const order = itemOrder[n.group.id];
    if (!order || order.length === 0) return n;
    const items = n.group.items
      .map((it, i) => ({ it, i }))
      .sort((a, b) => {
        const ra = rank(a.it.key, order);
        const rb = rank(b.it.key, order);
        return ra === rb ? a.i - b.i : ra - rb;
      })
      .map((x) => x.it);
    return { kind: 'group', group: { ...n.group, items } };
  });
}

/** Flat list of every item path the resolved tree exposes (selection-match helper). */
export function navItemForPath(ctx: NavGateContext, pathname: string): NavItem | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  let best: NavItem | null = null;
  let bestLen = -1;
  const consider = (it: NavItem) => {
    if (it.comingSoon) return;
    if (clean === it.path || clean.startsWith(it.path + '/')) {
      if (it.path.length > bestLen) { best = it; bestLen = it.path.length; }
    }
  };
  for (const node of resolveNav(ctx)) {
    if (node.kind === 'item') { consider(node.item); continue; }
    node.group.items.forEach(consider);
    (node.group.subgroups ?? []).forEach((sg) => sg.items.forEach(consider));
  }
  return best;
}
